import { useState, useRef, useEffect, useCallback } from 'react'
import {
  Box,
  Flex,
  VStack,
  HStack,
  Input,
  IconButton,
  Text,
  Spinner,
  Badge,
} from '@chakra-ui/react'
import { useAuth } from '@clerk/clerk-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useChat, Message } from '../hooks/useChat'
import { useDocuments, Document } from '../hooks/useDocuments'
import { api } from '../lib/api'

interface Contact {
  name: string
  email: string
}

type MentionItem =
  | { type: 'doc'; doc: Document }
  | { type: 'contact'; contact: Contact }

export default function ChatInterface() {
  const { messages, sendMessage, isLoading } = useChat()
  const { documents } = useDocuments()
  const { getToken } = useAuth()
  const [input, setInput] = useState('')
  const [mentionedDocs, setMentionedDocs] = useState<string[]>([])
  const [mentionedContacts, setMentionedContacts] = useState<Contact[]>([])
  const [showMentions, setShowMentions] = useState(false)
  const [mentionQuery, setMentionQuery] = useState('')
  const [contacts, setContacts] = useState<Contact[]>([])
  const [contactsLoading, setContactsLoading] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  // Build unified mention items list
  const filteredDocs = documents.filter((doc) =>
    doc.name.toLowerCase().includes(mentionQuery.toLowerCase())
  )

  const mentionItems: MentionItem[] = [
    ...filteredDocs.map((doc) => ({ type: 'doc' as const, doc })),
    ...contacts.map((contact) => ({ type: 'contact' as const, contact })),
  ]

  const hasItems = mentionItems.length > 0 || contactsLoading

  // Reset selectedIndex when items change
  useEffect(() => {
    setSelectedIndex(0)
  }, [mentionItems.length])

  const searchContacts = useCallback(async (query: string) => {
    if (query.length < 3) {
      setContacts([])
      return
    }
    setContactsLoading(true)
    try {
      const token = await getToken()
      const results = await api.searchContacts(query, token)
      setContacts(results)
    } catch (err) {
      console.error('Contact search error:', err)
      setContacts([])
    } finally {
      setContactsLoading(false)
    }
  }, [getToken])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setInput(value)

    // Check for @ mention — only trigger if @ is at start of input or preceded by a space
    const lastAtIndex = value.lastIndexOf('@')
    if (lastAtIndex !== -1 && (lastAtIndex === 0 || value[lastAtIndex - 1] === ' ')) {
      const query = value.slice(lastAtIndex + 1)
      if (!query.includes(' ')) {
        setMentionQuery(query)
        setShowMentions(true)

        // Debounced contact search
        if (debounceRef.current) clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(() => {
          searchContacts(query)
        }, 200)
        return
      }
    }
    setShowMentions(false)
    setContacts([])
  }

  const handleMentionSelect = (item: MentionItem) => {
    const lastAtIndex = input.lastIndexOf('@')
    if (item.type === 'doc') {
      const newInput = input.slice(0, lastAtIndex) + `@${item.doc.name} `
      setInput(newInput)
      setMentionedDocs([...mentionedDocs, item.doc.id])
    } else {
      const newInput = input.slice(0, lastAtIndex) + `@${item.contact.name} `
      setInput(newInput)
      setMentionedContacts([...mentionedContacts, item.contact])
    }
    setShowMentions(false)
    setContacts([])
    inputRef.current?.focus()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showMentions || !hasItems) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex((prev) => (prev + 1) % mentionItems.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex((prev) => (prev - 1 + mentionItems.length) % mentionItems.length)
    } else if (e.key === 'Enter' && mentionItems.length > 0) {
      e.preventDefault()
      handleMentionSelect(mentionItems[selectedIndex])
    } else if (e.key === 'Escape') {
      setShowMentions(false)
      setContacts([])
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!input.trim() || isLoading) return

    const query = input.trim()
    const docsToSend = [...mentionedDocs]
    const contactsToSend = [...mentionedContacts]
    setInput('')
    setMentionedDocs([])
    setMentionedContacts([])
    await sendMessage(query, docsToSend, contactsToSend)
  }

  return (
    <Flex direction="column" h="full">
      {/* Messages Area */}
      <Box flex={1} overflowY="auto" p={4}>
        <VStack gap={4} align="stretch" maxW="800px" mx="auto">
          {messages.length === 0 ? (
            <Box textAlign="center" py={12}>
              <Text fontSize="lg" color="gray.500">
                Ask a question about your documents
              </Text>
              <Text fontSize="sm" color="gray.400" mt={2}>
                Use @ to mention documents or contacts
              </Text>
            </Box>
          ) : (
            messages.map((message) => (
              <MessageBubble key={message.id} message={message} />
            ))
          )}
          {isLoading && (
            <HStack gap={2} p={4}>
              <Spinner size="sm" />
              <Text color="gray.500">Thinking...</Text>
            </HStack>
          )}
          <div ref={messagesEndRef} />
        </VStack>
      </Box>

      {/* Input Area */}
      <Box borderTopWidth="1px" borderColor="gray.200" bg="white" p={4}>
        <Box maxW="800px" mx="auto" position="relative">
          {/* Mention Dropdown */}
          {showMentions && hasItems && (
            <Box
              position="absolute"
              bottom="100%"
              left={0}
              right={0}
              bg="white"
              borderWidth="1px"
              borderColor="gray.200"
              borderRadius="md"
              shadow="lg"
              maxH="200px"
              overflowY="auto"
              mb={2}
            >
              {filteredDocs.length > 0 && (
                <Box px={3} py={1}>
                  <Text fontSize="xs" color="gray.400" fontWeight="semibold">Documents</Text>
                </Box>
              )}
              {filteredDocs.map((doc, i) => (
                <Box
                  key={doc.id}
                  px={4}
                  py={2}
                  cursor="pointer"
                  bg={selectedIndex === i ? 'gray.100' : undefined}
                  _hover={{ bg: 'gray.100' }}
                  onClick={() => handleMentionSelect({ type: 'doc', doc })}
                >
                  <Text fontSize="sm">{doc.name}</Text>
                </Box>
              ))}
              {(contacts.length > 0 || contactsLoading) && (
                <Box px={3} py={1}>
                  <Text fontSize="xs" color="gray.400" fontWeight="semibold">Contacts</Text>
                </Box>
              )}
              {contacts.map((contact, i) => {
                const idx = filteredDocs.length + i
                return (
                  <Box
                    key={contact.email}
                    px={4}
                    py={2}
                    cursor="pointer"
                    bg={selectedIndex === idx ? 'gray.100' : undefined}
                    _hover={{ bg: 'gray.100' }}
                    onClick={() => handleMentionSelect({ type: 'contact', contact })}
                  >
                    <Text fontSize="sm">{contact.name} &lt;{contact.email}&gt;</Text>
                  </Box>
                )
              })}
              {contactsLoading && (
                <HStack px={4} py={2} gap={2}>
                  <Spinner size="xs" />
                  <Text fontSize="sm" color="gray.400">Searching contacts...</Text>
                </HStack>
              )}
            </Box>
          )}

          {/* Mentioned Tags */}
          {(mentionedDocs.length > 0 || mentionedContacts.length > 0) && (
            <HStack gap={2} mb={2} flexWrap="wrap">
              {mentionedDocs.map((docId) => {
                const doc = documents.find((d) => d.id === docId)
                return doc ? (
                  <Badge key={docId} colorScheme="blue" variant="subtle">
                    @{doc.name}
                  </Badge>
                ) : null
              })}
              {mentionedContacts.map((contact) => (
                <Badge key={contact.email} colorScheme="blue" variant="subtle">
                  @{contact.name}
                </Badge>
              ))}
            </HStack>
          )}

          <form onSubmit={handleSubmit}>
            <HStack gap={2}>
              <Input
                ref={inputRef}
                value={input}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder="Ask a question... (use @ to mention documents or contacts)"
                size="lg"
              />
              <IconButton
                type="submit"
                aria-label="Send message"
                colorScheme="blue"
                size="lg"
                disabled={!input.trim() || isLoading}
              >
                <SendIcon />
              </IconButton>
            </HStack>
          </form>
        </Box>
      </Box>
    </Flex>
  )
}

function MessageBubble({ message }: { message: Message }) {
  const isUser = message.role === 'user'

  return (
    <Box
      alignSelf={isUser ? 'flex-end' : 'flex-start'}
      maxW="80%"
      p={4}
      borderRadius="lg"
      bg={isUser ? 'blue.500' : 'white'}
      color={isUser ? 'white' : 'gray.800'}
      borderWidth={isUser ? 0 : '1px'}
      borderColor="gray.200"
    >
      {isUser ? (
        <Text whiteSpace="pre-wrap">{message.content}</Text>
      ) : (
        <Box
          className="markdown-content"
          css={{
            '& p': { margin: '0.5em 0' },
            '& p:first-of-type': { marginTop: 0 },
            '& p:last-of-type': { marginBottom: 0 },
            '& strong': { fontWeight: 'bold' },
            '& em': { fontStyle: 'italic' },
            '& ul, & ol': { paddingLeft: '1.5em', margin: '0.5em 0' },
            '& li': { margin: '0.25em 0' },
            '& code': {
              background: 'rgba(0,0,0,0.06)',
              borderRadius: '3px',
              padding: '0.15em 0.3em',
              fontSize: '0.9em',
              fontFamily: 'monospace',
            },
            '& pre': {
              background: 'rgba(0,0,0,0.06)',
              borderRadius: '6px',
              padding: '0.75em 1em',
              overflow: 'auto',
              margin: '0.5em 0',
            },
            '& pre code': {
              background: 'none',
              padding: 0,
            },
            '& blockquote': {
              borderLeft: '3px solid',
              borderColor: 'gray.300',
              paddingLeft: '1em',
              margin: '0.5em 0',
              color: 'gray.600',
            },
            '& table': {
              borderCollapse: 'collapse',
              width: '100%',
              margin: '0.5em 0',
            },
            '& th, & td': {
              border: '1px solid',
              borderColor: 'gray.300',
              padding: '0.4em 0.8em',
              textAlign: 'left',
            },
            '& th': {
              background: 'rgba(0,0,0,0.04)',
              fontWeight: 'bold',
            },
            '& h1, & h2, & h3, & h4': {
              fontWeight: 'bold',
              margin: '0.75em 0 0.25em',
            },
            '& h1': { fontSize: '1.3em' },
            '& h2': { fontSize: '1.15em' },
            '& h3': { fontSize: '1.05em' },
            '& a': { color: 'blue.500', textDecoration: 'underline' },
            '& hr': { border: 'none', borderTop: '1px solid', borderColor: 'gray.200', margin: '0.75em 0' },
          }}
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {message.content}
          </ReactMarkdown>
        </Box>
      )}
      {message.sources && message.sources.length > 0 && (
        <VStack align="start" mt={3} pt={3} borderTopWidth="1px" borderColor="gray.200" gap={1}>
          <Text fontSize="xs" color={isUser ? 'blue.100' : 'gray.500'}>
            Sources:
          </Text>
          {message.sources.map((source, i) => (
            <Text key={i} fontSize="xs" color={isUser ? 'blue.100' : 'gray.500'}>
              • {source.document} (page {source.page})
            </Text>
          ))}
        </VStack>
      )}
      {message.webSources && message.webSources.length > 0 && (
        <VStack align="start" mt={3} pt={3} borderTopWidth="1px" borderColor="gray.200" gap={1}>
          <Text fontSize="xs" color={isUser ? 'blue.100' : 'gray.500'}>
            Web Sources:
          </Text>
          {message.webSources.map((source, i) => (
            <Text key={i} fontSize="xs" color={isUser ? 'blue.100' : 'gray.500'}>
              • <a href={source.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline' }}>{source.title}</a>
            </Text>
          ))}
        </VStack>
      )}
    </Box>
  )
}

function SendIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
    </svg>
  )
}
